import { useEffect, useRef } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type PathValue,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormTrigger,
    type UseFormWatch,
} from 'react-hook-form';

import { ShowVAT } from '@/api/schema';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

type EInvoicingFieldsProps<T extends FieldValues> = {
    control: Control<T>;
    register: UseFormRegister<T>;
    watch: UseFormWatch<T>;
    setValue: UseFormSetValue<T>;
    trigger: UseFormTrigger<T>;
    schemeIdName: FieldPath<T>;
    participantIdentifierName: FieldPath<T>;
    vatName: FieldPath<T>;
    schemeIdError?: string;
    participantIdentifierError?: string;
    disabled?: boolean;
    rowClassName?: string;
};

const EInvoicingFields = <T extends FieldValues>({
    control,
    register,
    watch,
    setValue,
    trigger,
    schemeIdName,
    participantIdentifierName,
    vatName,
    schemeIdError,
    participantIdentifierError,
    disabled = false,
    rowClassName = 'display-flex margin-bottom-5',
}: EInvoicingFieldsProps<T>) => {
    const { eInvoicingDetails } = useUserStore();

    const schemeId = watch(schemeIdName) as string | undefined;
    const participantIdentifier = watch(participantIdentifierName) as string | undefined;
    const vat = watch(vatName) as string | undefined;

    const selectedScheme = eInvoicingDetails?.find(s => s.scheme === schemeId);
    const isParticipantIdentifierAutoFilled = !!selectedScheme?.showVAT;
    const prevSchemeId = useRef<string | undefined>(undefined);
    const prevVat = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (disabled || !schemeId || !vat) {
            return;
        }

        const isFirstRun = prevSchemeId.current === undefined;
        const schemeChanged = schemeId !== prevSchemeId.current;
        const vatChanged = vat !== prevVat.current;
        prevSchemeId.current = schemeId;
        prevVat.current = vat;

        if (!selectedScheme?.showVAT) {
            if (schemeChanged && !isFirstRun) {
                setValue(participantIdentifierName, '' as PathValue<T, FieldPath<T>>, { shouldDirty: true });
                trigger(participantIdentifierName);
            }
            return;
        }
        if (schemeChanged || vatChanged) {
            const identifier = selectedScheme.showVAT === ShowVAT.enum.minimal ? vat.slice(2) : vat;
            setValue(participantIdentifierName, identifier as PathValue<T, FieldPath<T>>, { shouldDirty: true });
            trigger(participantIdentifierName);
        }
    }, [schemeId, vat, selectedScheme, setValue, trigger, disabled, participantIdentifierName]);

    return (
        <>
            <label>{t('general.eInvoicingId')}</label>
            <div className={rowClassName}>
                <div className='display-flex align-items-end gap-5 flex-basis-33pct'>
                    <Controller
                        name={schemeIdName}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <SelectInput
                                required
                                className='flex-1-1-0'
                                error={schemeIdError}
                                label={t('general.schemeId')}
                                selectAttributes={{
                                    placeholder: t('onboarding.invoicingDetails.eInvoicing.placeholder'),
                                    disabled,
                                    onChange: (item: { id: string } | undefined) => onChange(item?.id),
                                    value: value ? [value] : undefined,
                                    options: eInvoicingDetails?.map(({ scheme }) => ({
                                        id: scheme,
                                        label: scheme,
                                    })),
                                }}
                            />
                        )}
                    />
                    <span style={{ marginBottom: '8px' }}>:</span>
                    <Input
                        required
                        className='flex-1-1-0'
                        label={t('general.participantIdentifier')}
                        error={participantIdentifierError}
                        inputAttributes={{
                            ...register(participantIdentifierName, {
                                onChange: () => trigger(participantIdentifierName),
                            }),
                            disabled: disabled || isParticipantIdentifierAutoFilled,
                        }}
                    />
                </div>
            </div>
            {!(schemeId && participantIdentifier) && (
                <span className='text-color-gray'>{t('onboarding.invoicingDetails.eInvoicing.note')}</span>
            )}
        </>
    );
};

export default EInvoicingFields;
